package er.prateek.crossly.ui.screens.results

import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.Spring
import androidx.compose.animation.core.spring
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import crossly.shared.generated.resources.Res
import crossly.shared.generated.resources.results_back_to_home
import crossly.shared.generated.resources.results_back_to_levels
import crossly.shared.generated.resources.results_daily_streak
import crossly.shared.generated.resources.results_next_level
import crossly.shared.generated.resources.results_play_again
import crossly.shared.generated.resources.results_rank_flawless
import crossly.shared.generated.resources.results_rank_flawless_detail
import crossly.shared.generated.resources.results_rank_great
import crossly.shared.generated.resources.results_rank_great_detail
import crossly.shared.generated.resources.results_rank_solved
import crossly.shared.generated.resources.results_rank_solved_detail
import crossly.shared.generated.resources.results_challenge_another
import crossly.shared.generated.resources.results_challenge_friend
import crossly.shared.generated.resources.results_lost
import crossly.shared.generated.resources.results_lost_by
import crossly.shared.generated.resources.results_share_with
import crossly.shared.generated.resources.results_time_taken
import crossly.shared.generated.resources.results_tie
import crossly.shared.generated.resources.results_won
import crossly.shared.generated.resources.results_won_by
import er.prateek.crossly.ads.AdPlacement
import er.prateek.crossly.ads.AdTrigger
import er.prateek.crossly.di.AppContainer
import er.prateek.crossly.firebase.AnalyticsEvents
import er.prateek.crossly.share.challengeWon
import er.prateek.crossly.share.formatSpokenDuration
import er.prateek.crossly.share.timeMarginMs
import er.prateek.crossly.ui.share.NamePromptDialog
import er.prateek.crossly.ui.components.ConfettiBurst
import er.prateek.crossly.ui.components.SparkBurst
import er.prateek.crossly.ui.components.GameButton
import er.prateek.crossly.ui.components.GameButtonSize
import er.prateek.crossly.ui.components.GameButtonStyle
import er.prateek.crossly.ui.components.GameCard
import er.prateek.crossly.ui.components.GameScreen
import er.prateek.crossly.ui.components.IconPlay
import er.prateek.crossly.ui.components.IconReplay
import er.prateek.crossly.ui.components.IconTrophy
import er.prateek.crossly.ui.components.StarRating
import er.prateek.crossly.ui.components.StreakBadge
import er.prateek.crossly.ui.theme.Spacing
import er.prateek.crossly.ui.theme.game
import kotlinx.coroutines.delay
import org.jetbrains.compose.resources.stringResource

/**
 * Long enough for the trophy spring and the star stamps to land before an interstitial can
 * cover them.
 */
private const val REWARD_BEAT_MS = 1_400L
private const val NOTIFICATION_PROMPT_EXTRA_DELAY_MS = 1_500L

@Composable
fun ResultsScreen(
    puzzleId: String,
    stars: Int,
    onPlayAgain: (String) -> Unit,
    onNextLevel: (String) -> Unit,
    onBackToLevels: () -> Unit,
    isDaily: Boolean = false,
    fromChallenge: Boolean = false,
    challengerName: String = "",
    elapsedMs: Long = 0,
    challengerTimeMs: Long = 0,
) {
    val colors = MaterialTheme.game
    val share = AppContainer.shareManager
    var puzzleTitle by remember(puzzleId) { mutableStateOf<String?>(null) }
    var nextPuzzleId by remember(puzzleId) { mutableStateOf<String?>(null) }
    var puzzleLabel by remember(puzzleId) { mutableStateOf<String?>(null) }
    var pendingShare by remember { mutableStateOf<ShareKind?>(null) }

    LaunchedEffect(puzzleId, isDaily) {
        val puzzle = AppContainer.contentRepository.puzzleById(puzzleId)
        puzzleTitle = puzzle?.title
        // "Next level" only makes sense for tier progression -- Daily is a
        // separate track, not a position within the EASY tier's level list.
        if (puzzle != null && !isDaily) {
            val tierPuzzles = AppContainer.contentRepository.puzzlesByTier(puzzle.difficulty)
            val index = tierPuzzles.indexOfFirst { it.id == puzzleId }
            nextPuzzleId = tierPuzzles.getOrNull(index + 1)?.id
            puzzleLabel = share.puzzleLabel(puzzleId)
        }
        val bestTimeMs = AppContainer.progressionManager.bestTimeMs(puzzleId) ?: elapsedMs
        AppContainer.analytics.logEvent(
            AnalyticsEvents.RESULTS_VIEWED,
            mapOf(
                "puzzle_id" to puzzleId,
                "tier" to (puzzle?.difficulty?.name ?: ""),
                "is_daily" to isDaily,
                "stars" to stars,
                "elapsed_ms" to elapsedMs,
                "is_personal_best" to (elapsedMs > 0 && elapsedMs <= bestTimeMs),
                "from_challenge" to fromChallenge,
                "challenge_won" to (fromChallenge && challengerTimeMs > 0 && elapsedMs < challengerTimeMs),
                "challenge_tied" to (fromChallenge && challengerTimeMs > 0 && elapsedMs == challengerTimeMs),
                "has_next_level" to (nextPuzzleId != null),
            ),
        )
    }

    // The interstitial checkpoint. Deliberately delayed past the reward beat below: an ad
    // that lands on top of the trophy and stars takes away the moment the player just
    // earned, and is the single fastest way to make an ad-supported game feel hostile.
    // AdsCoordinator decides whether anything is actually shown -- most solves show nothing.
    LaunchedEffect(puzzleId, isDaily) {
        delay(REWARD_BEAT_MS)
        AppContainer.adsCoordinator.onPuzzleCompleted(
            if (isDaily) AdTrigger.DAILY_COMPLETED else AdTrigger.PUZZLE_COMPLETED,
        )
    }

    // The OS notification permission prompt's one deliberate "moment of investment" --
    // gated by session/puzzle-completion thresholds in Remote Config, and only ever shown
    // once (NotificationCoordinator.shouldRequestOsPermission() checks hasRequestedOsPermission()).
    // Delayed past the interstitial checkpoint above so the two prompts don't collide.
    LaunchedEffect(puzzleId, isDaily) {
        delay(REWARD_BEAT_MS + NOTIFICATION_PROMPT_EXTRA_DELAY_MS)
        if (AppContainer.notificationCoordinator.shouldRequestOsPermission()) {
            AppContainer.notificationCoordinator.requestOsPermission()
        }
    }

    // Trophy drops in ahead of the stars, which then stamp one by one — the
    // whole screen is paced as a short reward beat rather than a static summary.
    val trophyScale = remember(puzzleId) { Animatable(0f) }
    LaunchedEffect(puzzleId) {
        trophyScale.animateTo(
            targetValue = 1f,
            animationSpec = spring(dampingRatio = Spring.DampingRatioMediumBouncy, stiffness = Spring.StiffnessLow),
        )
    }

    val timedChallenge = fromChallenge && challengerTimeMs > 0
    val wonChallenge = challengeWon(elapsedMs, challengerTimeMs)
    val tiedChallenge = timedChallenge && elapsedMs == challengerTimeMs
    val marginSpoken = formatSpokenDuration(
        if (wonChallenge) timeMarginMs(elapsedMs, challengerTimeMs)
        else timeMarginMs(challengerTimeMs, elapsedMs),
    )
    val timeSpoken = formatSpokenDuration(elapsedMs)
    val bestTimeMs = AppContainer.progressionManager.bestTimeMs(puzzleId) ?: elapsedMs

    val rank = when (stars) {
        3 -> Res.string.results_rank_flawless to Res.string.results_rank_flawless_detail
        2 -> Res.string.results_rank_great to Res.string.results_rank_great_detail
        else -> Res.string.results_rank_solved to Res.string.results_rank_solved_detail
    }
    val headline = when {
        wonChallenge -> stringResource(Res.string.results_won)
        tiedChallenge -> stringResource(Res.string.results_tie)
        timedChallenge -> stringResource(Res.string.results_lost)
        else -> stringResource(rank.first)
    }
    val detail = when {
        wonChallenge -> stringResource(Res.string.results_won_by, challengerName, marginSpoken)
        tiedChallenge -> stringResource(Res.string.results_time_taken, timeSpoken)
        timedChallenge -> stringResource(Res.string.results_lost_by, challengerName, marginSpoken)
        else -> stringResource(rank.second)
    }

    GameScreen(adPlacement = AdPlacement.RESULTS) {
        Box(modifier = Modifier.fillMaxSize()) {
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .verticalScroll(rememberScrollState()),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(Spacing.lg, Alignment.CenterVertically),
            ) {
                Box(
                    modifier = Modifier
                        .size(96.dp)
                        .graphicsLayer {
                            scaleX = trophyScale.value
                            scaleY = trophyScale.value
                            rotationZ = (1f - trophyScale.value) * -25f
                        },
                    contentAlignment = Alignment.Center,
                ) {
                    IconTrophy(size = 96.dp, tint = colors.gold)
                }

                Text(
                    text = headline,
                    style = MaterialTheme.typography.displaySmall,
                    color = colors.ink,
                    textAlign = TextAlign.Center,
                )

                StarRating(stars = stars, starSize = 46.dp, spacing = Spacing.sm, animated = true)

                GameCard(modifier = Modifier.widthIn(max = 420.dp).fillMaxWidth()) {
                    puzzleTitle?.let {
                        Text(
                            text = it,
                            style = MaterialTheme.typography.titleMedium,
                            color = colors.ink,
                            textAlign = TextAlign.Center,
                            modifier = Modifier.fillMaxWidth(),
                        )
                    }
                    Text(
                        text = detail,
                        style = MaterialTheme.typography.bodyMedium,
                        color = colors.inkMuted,
                        textAlign = TextAlign.Center,
                        modifier = Modifier.fillMaxWidth().padding(top = Spacing.xs),
                    )
                    if (elapsedMs > 0 && !tiedChallenge) {
                        Text(
                            text = stringResource(Res.string.results_time_taken, timeSpoken),
                            style = MaterialTheme.typography.titleMedium,
                            color = colors.gold,
                            textAlign = TextAlign.Center,
                            modifier = Modifier.fillMaxWidth().padding(top = Spacing.sm),
                        )
                    }
                    if (isDaily) {
                        val streak = AppContainer.streakManager.displayStreak()
                        Box(
                            modifier = Modifier.fillMaxWidth().padding(top = Spacing.md),
                            contentAlignment = Alignment.Center,
                        ) {
                            StreakBadge(streak = streak)
                        }
                    }
                }

                Column(
                    modifier = Modifier.widthIn(max = 420.dp).fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(Spacing.sm),
                ) {
                    nextPuzzleId?.let { next ->
                        GameButton(
                            text = stringResource(Res.string.results_next_level),
                            onClick = { onNextLevel(next) },
                            style = GameButtonStyle.PRIMARY,
                            size = GameButtonSize.LARGE,
                            fillWidth = true,
                            icon = { tint -> IconPlay(size = 17.dp, tint = tint) },
                        )
                    }
                    GameButton(
                        text = stringResource(Res.string.results_play_again),
                        onClick = { onPlayAgain(puzzleId) },
                        style = GameButtonStyle.SECONDARY,
                        fillWidth = true,
                        icon = { tint -> IconReplay(size = 18.dp, tint = tint) },
                    )
                    GameButton(
                        text = stringResource(
                            if (isDaily) Res.string.results_back_to_home else Res.string.results_back_to_levels,
                        ),
                        onClick = onBackToLevels,
                        style = GameButtonStyle.GHOST,
                        fillWidth = true,
                    )
                    if (!isDaily) {
                        if (fromChallenge) {
                            if (wonChallenge) {
                                GameButton(
                                    text = stringResource(Res.string.results_share_with, challengerName),
                                    onClick = {
                                        AppContainer.analytics.logEvent(
                                            AnalyticsEvents.SHARE_BUTTON_TAPPED,
                                            mapOf("puzzle_id" to puzzleId, "kind" to "win_vs_referrer"),
                                        )
                                        pendingShare = ShareKind.WON
                                    },
                                    style = GameButtonStyle.GOLD,
                                    fillWidth = true,
                                )
                            }
                            GameButton(
                                text = stringResource(Res.string.results_challenge_another),
                                onClick = {
                                    AppContainer.analytics.logEvent(
                                        AnalyticsEvents.SHARE_BUTTON_TAPPED,
                                        mapOf("puzzle_id" to puzzleId, "kind" to "challenge_another"),
                                    )
                                    pendingShare = ShareKind.CHALLENGE
                                },
                                style = GameButtonStyle.SECONDARY,
                                fillWidth = true,
                            )
                        } else {
                            GameButton(
                                text = stringResource(Res.string.results_challenge_friend),
                                onClick = {
                                    AppContainer.analytics.logEvent(
                                        AnalyticsEvents.SHARE_BUTTON_TAPPED,
                                        mapOf("puzzle_id" to puzzleId, "kind" to "challenge_friend"),
                                    )
                                    pendingShare = ShareKind.CHALLENGE
                                },
                                style = GameButtonStyle.GOLD,
                                fillWidth = true,
                            )
                        }
                    }
                }
            }

            // Purely decorative and drawn last so it never intercepts taps on
            // the buttons underneath.
            if (wonChallenge) {
                SparkBurst(
                    modifier = Modifier.fillMaxSize(),
                    seed = puzzleId.hashCode() + stars + 7,
                )
            }
            if (!timedChallenge || wonChallenge) {
                ConfettiBurst(
                    modifier = Modifier.fillMaxSize(),
                    seed = puzzleId.hashCode() + stars,
                    pieceCount = if (stars >= 3 || wonChallenge) 72 else 44,
                )
            }
        }
    }

    val shareKind = pendingShare
    if (shareKind != null && !share.hasPlayerName()) {
        LaunchedEffect(shareKind) {
            AppContainer.analytics.logEvent(AnalyticsEvents.NAME_PROMPT_SHOWN, mapOf("source" to "results_share"))
        }
        NamePromptDialog(
            initialName = share.playerName(),
            skipAllowed = false,
            onSave = { name ->
                share.savePlayerName(name)
                AppContainer.analytics.logEvent(AnalyticsEvents.NAME_SAVED, mapOf("source" to "results_share"))
                runShare(shareKind, puzzleId, puzzleLabel, challengerName, elapsedMs, challengerTimeMs, bestTimeMs)
                pendingShare = null
            },
            onSkip = {
                AppContainer.analytics.logEvent(AnalyticsEvents.NAME_SKIPPED, mapOf("source" to "results_share"))
                pendingShare = null
            },
            onDismiss = { pendingShare = null },
        )
    } else if (shareKind != null) {
        LaunchedEffect(shareKind) {
            runShare(shareKind, puzzleId, puzzleLabel, challengerName, elapsedMs, challengerTimeMs, bestTimeMs)
            pendingShare = null
        }
    }
}

private enum class ShareKind { CHALLENGE, WON }

private fun runShare(
    kind: ShareKind,
    puzzleId: String,
    puzzleLabel: String?,
    challengerName: String,
    elapsedMs: Long,
    challengerTimeMs: Long,
    bestTimeMs: Long,
) {
    val share = AppContainer.shareManager
    val name = share.playerName()
    val label = puzzleLabel ?: puzzleId
    when (kind) {
        ShareKind.CHALLENGE -> share.shareChallenge(name, puzzleId, label, bestTimeMs)
        ShareKind.WON -> share.shareWinWithReferrer(
            name = name,
            referrerName = challengerName,
            puzzleId = puzzleId,
            puzzleLabel = label,
            myTimeMs = elapsedMs,
            theirTimeMs = challengerTimeMs,
        )
    }
}
